import { useWidgetContext } from 'widgets/WidgetContext';
import {
    useFetchUserPreference,
    useUpdateUserPreference,
    useUserPreference,
} from 'userPreferences/hooks';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import { useCallback } from 'react';

export type EditedColumnNames = {
    [colId: string]: string;
};

const defaultColumnWidths: EditedColumnNames = {};
const DefaultGridId = 'default';

function useWidgetKey(gridId = DefaultGridId): string {
    const { widgetType } = useWidgetContext();
    return `editedColumnNames-${widgetType}-${gridId}`;
}

type OnEditedColumnNameCb = (
    colId: string,
    nextEditedColumnName: string
) => void;
type UseWidgetEditedColumnNamesRet = {
    editedColumnNames: EditedColumnNames;
    onEditedColumnName?: OnEditedColumnNameCb;
};

export function useWidgetEditedColumnNames(
    gridId = DefaultGridId
): UseWidgetEditedColumnNamesRet {
    const key = useWidgetKey(gridId);
    const locked = useSelectLocked();

    useFetchUserPreference(key);

    const userPref = useUserPreference<EditedColumnNames>(key);
    const updateUserPref = useUpdateUserPreference<EditedColumnNames>(key);

    const editedColumnNames = userPref ?? defaultColumnWidths;

    const onEditedColumnName = useCallback(
        (colId: string, nextEditedColumnName: string) => {
            const nextColumnNames = {
                ...editedColumnNames,
                [colId]: nextEditedColumnName,
            };
            updateUserPref(nextColumnNames);
        },
        [editedColumnNames, updateUserPref]
    );

    return {
        editedColumnNames,
        onEditedColumnName: locked ? undefined : onEditedColumnName,
    };
}
